import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getMonth,
  getYear,
} from 'date-fns'
import { DateTime } from 'luxon'
import moment from 'moment'
import type { Moment } from 'moment'

const APPT_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

export function getAppointmentLocalMoment(appt: {
  appointment_start_date?: string
  appt_date?: string
}): Moment {
  const start = appt.appointment_start_date?.trim()
  if (start) {
    if (APPT_DATE_ONLY_RE.test(start)) {
      return moment(start, 'YYYY-MM-DD', true)
    }
    return moment(start)
  }
  const d = appt.appt_date?.trim() ?? ''
  if (APPT_DATE_ONLY_RE.test(d)) {
    return moment(d, 'YYYY-MM-DD', true)
  }
  return moment(d)
}

export function formatAppointmentLocalDate(
  appt: {
    appointment_start_date?: string
    appt_date?: string
  },
  pattern: string,
): string {
  return getAppointmentLocalMoment(appt).format(pattern)
}

export function getAppointmentLocalDateYmd(apt: {
  appointment_start_date?: string
  appt_date?: string
}): string {
  return formatAppointmentLocalDate(apt, 'YYYY-MM-DD')
}

export function formatAppointmentLocalStartTime(appt: {
  appointment_start_date?: string
  appt_date?: string
  appt_start?: string
}): string {
  const startRaw = appt.appointment_start_date?.trim() ?? ''
  if (startRaw && !APPT_DATE_ONLY_RE.test(startRaw)) {
    return moment(startRaw).format('hh:mm A')
  }
  const t = appt.appt_start?.trim() ?? ''
  if (!t) {
    return startRaw
      ? moment(startRaw, 'YYYY-MM-DD', true).format('hh:mm A')
      : ''
  }
  const datePart =
    startRaw && APPT_DATE_ONLY_RE.test(startRaw)
      ? startRaw
      : (appt.appt_date?.trim() ?? '')
  if (APPT_DATE_ONLY_RE.test(datePart)) {
    const combined = moment(
      `${datePart} ${t}`,
      ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD H:mm'],
      true,
    )
    if (combined.isValid()) {
      return combined.format('hh:mm A')
    }
  }
  const parsedT = moment(t, ['HH:mm:ss', 'HH:mm', 'hh:mm A'], true)
  if (parsedT.isValid()) {
    return parsedT.format('hh:mm A')
  }
  return t
}

export const format12HourTime = (time: number): string => {
  const hour = parseInt(time.toString(), 10)
  const hour12 = hour % 12 || 12 // Convert to 12-hour format
  const ampm = hour < 12 ? 'AM' : 'PM'
  return `${hour12}:00 ${ampm}`
}

// This function returns all the days of a given month and year as strings
export const getDaysOfMonth = (month: number, year?: number): string[] => {
  const effectiveYear = year ?? getYear(new Date()) // Use provided year or default to the current year
  const date = new Date(effectiveYear, month - 1)
  const start = startOfMonth(date)
  const end = endOfMonth(date)

  return eachDayOfInterval({ start, end }).map((day) =>
    day.getDate().toString(),
  )
}

// This array contains all the years from 1980 up to the current year
export const getYears = Array.from(
  { length: getYear(new Date()) - 1980 + 1 },
  (_, index) => 1980 + index,
)

// This function returns the months of the year considering the specified year and avoiding future months in the current year
export const getMonthsOfYear = (
  year: number,
): { month: string; monthInNumber: number }[] => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const currentDate = new Date()
  const currentYear = getYear(currentDate)
  const currentMonth = getMonth(currentDate)

  const months = monthNames.map((month, index) => ({
    month,
    monthInNumber: index + 1, // Adjust the index to get the month number starting at 1
  }))

  return year === currentYear ? months.slice(0, currentMonth + 1) : months
}

export const formatDateTime = (
  date: Date | string,
  format = 'YYYY-MM-DD HH:mm:ss',
): string => {
  return moment(date).format(format)
}

export const calculateDateRange = (
  tab: string,
  selectedDate: string | Date,
): { startDate: string; endDate: string } => {
  const parsedDate = moment(selectedDate, 'MMMM D, YYYY')

  if (tab === 'week') {
    const startOfWeek = parsedDate.clone().startOf('week')
    const endOfWeek = parsedDate.clone().endOf('week')
    return {
      startDate: startOfWeek.format('YYYY-MM-DD'),
      endDate: endOfWeek.format('YYYY-MM-DD'),
    }
  } else if (tab === 'month') {
    const startMonth = parsedDate.clone().startOf('month')
    const endMonth = parsedDate.clone().endOf('month')
    return {
      startDate: startMonth.format('YYYY-MM-DD'),
      endDate: endMonth.format('YYYY-MM-DD'),
    }
  }

  return {
    startDate: parsedDate.format('YYYY-MM-DD'),
    endDate: parsedDate.format('YYYY-MM-DD'),
  }
}

export const formatMinuteTime = (time: number): string => {
  const minutes = Math.floor(time / 60)
    .toString()
    .padStart(2, '0')
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, '0')
  return `${minutes}:${seconds}`
}

// Sample Output: 03:42 or 01:03:42
export const formatSecondsToString = (time: number): string => {
  const hours = Math.floor(time / 3600)
  const minutes = Math.floor((time % 3600) / 60)
  const seconds = time % 60

  const mm = String(minutes).padStart(2, '0')
  const ss = String(seconds).padStart(2, '0')

  if (hours >= 1) {
    const hh = String(hours).padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  }

  return `${mm}:${ss}`
}

// // Sample output for getHourAmPm: 8AM / 8am
export const getHourAmPm = (_date: Date, upperCase?: boolean): string => {
  const date = new Date(_date)
  const hours = date.getHours()
  let period = hours >= 12 ? 'pm' : 'am'
  if (upperCase) period = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 || 12
  return `${hour12}${period}`
}

// // Sample output for 1PM: 13
export const getMilitaryHour = (time: string): number => {
  const number = parseInt(time.slice(0, -2))
  const str = time.slice(-2).toLowerCase()

  const response = str === 'am' ? number : number + 12
  return response
}

export const formatMilitaryTimeTo12Hour = (
  militaryTime: string | undefined,
): string => {
  if (!militaryTime) return ''

  try {
    const [hours, minutes] = militaryTime.split(':')
    const hour = parseInt(hours, 10)
    const minute = parseInt(minutes, 10)

    if (isNaN(hour) || isNaN(minute)) return militaryTime

    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    const displayMinute = minute.toString().padStart(2, '0')

    return `${displayHour}:${displayMinute} ${period}`
  } catch (error) {
    return militaryTime
  }
}

export const toUTC = (dateStr: Date, timezone: string): Date => {
  const dateTime = DateTime.fromJSDate(dateStr, { zone: timezone })

  if (!dateTime.isValid) {
    throw new Error(`Invalid date format: ${dateStr} or timezone: ${timezone}`)
  }

  return dateTime.toUTC().toJSDate()
}

export const setTime = (date: Date, time: string): Date => {
  const newDate = new Date(date)
  const [hours, minutes] = time.split(':').map(Number) as [number, number]
  newDate.setHours(hours)
  newDate.setMinutes(minutes)
  return newDate
}
